import React, { useRef } from "react";
import { Camera, Film, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

interface OptionPickerProps {
  onFileSelected: (file: File) => void;
  maxFileSizeMB?: number;
  setOption: (option: string) => void;
  onMediaCaptured?: (file: File) => void;
}

const OptionPicker: React.FC<OptionPickerProps> = ({
  onFileSelected,
  maxFileSizeMB = 10,
  setOption,
  onMediaCaptured,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const validateFile = (file: File): boolean => {
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      console.error(`File too large. Maximum size is ${maxFileSizeMB}MB.`);
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        onFileSelected(file);
        onMediaCaptured?.(file);
      }
    }
  };

  const handleOpenCamera = () => {
    if (isMobile) {
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } else {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const videoElement = document.createElement("video");
        const canvasElement = document.createElement("canvas");
        const stream = document.createElement("div");

        stream.style.position = "fixed";
        stream.style.top = "0";
        stream.style.left = "0";
        stream.style.width = "100%";
        stream.style.height = "100%";
        stream.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        stream.style.zIndex = "9999";
        stream.style.display = "flex";
        stream.style.flexDirection = "column";
        stream.style.alignItems = "center";
        stream.style.justifyContent = "center";

        videoElement.style.maxWidth = "100%";
        videoElement.style.maxHeight = "80vh";
        videoElement.style.objectFit = "contain";
        videoElement.autoplay = true;

        const controls = document.createElement("div");
        controls.style.marginTop = "20px";
        controls.style.display = "flex";
        controls.style.gap = "10px";

        const captureBtn = document.createElement("button");
        captureBtn.textContent = "Capture Photo";
        captureBtn.style.padding = "10px 20px";
        captureBtn.style.backgroundColor = "var(--primary)";
        captureBtn.style.color = "white";
        captureBtn.style.border = "none";
        captureBtn.style.borderRadius = "4px";
        captureBtn.style.cursor = "pointer";

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.padding = "10px 20px";
        cancelBtn.style.backgroundColor = "#f44336";
        cancelBtn.style.color = "white";
        cancelBtn.style.border = "none";
        cancelBtn.style.borderRadius = "4px";
        cancelBtn.style.cursor = "pointer";

        controls.appendChild(captureBtn);
        controls.appendChild(cancelBtn);

        stream.appendChild(videoElement);
        stream.appendChild(controls);

        document.body.appendChild(stream);

        let mediaStream: MediaStream | null = null;

        navigator.mediaDevices
          .getUserMedia({ video: { facingMode: "environment" } })
          .then((stream) => {
            mediaStream = stream;
            videoElement.srcObject = stream;
          })
          .catch((error) => {
            console.error("Camera access error:", error);
            document.body.removeChild(stream);
          });

        const cleanup = () => {
          if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
          }
          if (document.body.contains(stream)) {
            document.body.removeChild(stream);
          }
        };

        cancelBtn.addEventListener("click", cleanup);

        captureBtn.addEventListener("click", () => {
          canvasElement.width = videoElement.videoWidth;
          canvasElement.height = videoElement.videoHeight;

          const context = canvasElement.getContext("2d");
          if (context) {
            context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

            canvasElement.toBlob(
              (blob) => {
                if (blob) {
                  const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
                  if (validateFile(file)) {
                    onFileSelected(file);
                    onMediaCaptured?.(file);
                  }
                  cleanup();
                }
              },
              "image/jpeg",
              0.95
            );
          }
        });
      }
    }
  };

  const handleOpenVideoCamera = () => {
    if (isMobile) {
      if (videoInputRef.current) {
        videoInputRef.current.click();
      }
    } else {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const videoElement = document.createElement("video");
        const stream = document.createElement("div");
        let mediaRecorder: MediaRecorder | null = null;
        let recordedChunks: Blob[] = [];

        stream.style.position = "fixed";
        stream.style.top = "0";
        stream.style.left = "0";
        stream.style.width = "100%";
        stream.style.height = "100%";
        stream.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        stream.style.zIndex = "9999";
        stream.style.display = "flex";
        stream.style.flexDirection = "column";
        stream.style.alignItems = "center";
        stream.style.justifyContent = "center";

        videoElement.style.maxWidth = "100%";
        videoElement.style.maxHeight = "80vh";
        videoElement.style.objectFit = "contain";
        videoElement.autoplay = true;

        const controls = document.createElement("div");
        controls.style.marginTop = "20px";
        controls.style.display = "flex";
        controls.style.gap = "10px";

        const recordBtn = document.createElement("button");
        recordBtn.textContent = "Start Recording";
        recordBtn.style.padding = "10px 20px";
        recordBtn.style.backgroundColor = "var(--primary)";
        recordBtn.style.color = "white";
        recordBtn.style.border = "none";
        recordBtn.style.borderRadius = "4px";
        recordBtn.style.cursor = "pointer";

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.padding = "10px 20px";
        cancelBtn.style.backgroundColor = "#f44336";
        cancelBtn.style.color = "white";
        cancelBtn.style.border = "none";
        cancelBtn.style.borderRadius = "4px";
        cancelBtn.style.cursor = "pointer";

        controls.appendChild(recordBtn);
        controls.appendChild(cancelBtn);

        stream.appendChild(videoElement);
        stream.appendChild(controls);

        document.body.appendChild(stream);

        let mediaStream: MediaStream | null = null;

        navigator.mediaDevices
          .getUserMedia({ video: true, audio: true })
          .then((stream) => {
            mediaStream = stream;
            videoElement.srcObject = stream;

            mediaRecorder = new MediaRecorder(stream);

            mediaRecorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                recordedChunks.push(event.data);
              }
            };

            mediaRecorder.onstop = () => {
              const blob = new Blob(recordedChunks, { type: "video/webm" });
              const file = new File([blob], `video_${Date.now()}.webm`, { type: "video/webm" });
              if (validateFile(file)) {
                onFileSelected(file);
                onMediaCaptured?.(file);
              }
              cleanup();
            };
          })
          .catch((error) => {
            console.error("Camera access error:", error);
            document.body.removeChild(stream);
          });

        const cleanup = () => {
          if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
          }
          if (document.body.contains(stream)) {
            document.body.removeChild(stream);
          }
        };

        let isRecording = false;
        recordBtn.addEventListener("click", () => {
          if (!mediaRecorder) return;

          if (!isRecording) {
            recordedChunks = [];
            mediaRecorder.start();
            recordBtn.textContent = "Stop Recording";
            recordBtn.style.backgroundColor = "#f44336";
            isRecording = true;
          } else {
            mediaRecorder.stop();
            recordBtn.textContent = "Processing...";
            recordBtn.disabled = true;
            isRecording = false;
          }
        });

        cancelBtn.addEventListener("click", cleanup);
      }
    }
  };

  return (
    <div className="flex flex-col space-y-3">
      <Button
        type="button"
        variant="secondary"
        className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
        onClick={handleOpenCamera}
      >
        <Camera className="h-6 w-6" />
        Take a Photo
      </Button>

      <Button
        type="button"
        variant="secondary"
        className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
        onClick={handleOpenVideoCamera}
      >
        <Film className="h-6 w-6" />
        Take a Video
      </Button>

      <Button
        type="button"
        variant="secondary"
        className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
        onClick={() => setOption("upload")}
      >
        <Upload className="h-6 w-6" />
        Upload Photo/Video
      </Button>

      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileChange}
        accept="image/*"
        capture="environment"
        className="hidden"
      />

      <input
        type="file"
        ref={videoInputRef}
        onChange={handleFileChange}
        accept="video/*"
        capture="environment"
        className="hidden"
      />
    </div>
  );
};

export default OptionPicker;
